import { Vehicle } from "../../types";
import Reviews from "./components/Reviews/Reviews";
import VehicleHeader from "./components/VehicleHeader";
import RatingCriteria from "./components/RatingCriteria";
import SectionHeading from "./components/SectionHeading";
import { Head, Link, router } from "@inertiajs/react";
import { Calendar, ImageOff } from "lucide-react";
import useVehicleCompare from "../../utils/VehicleCompareProvider";
import { resolveTestDriveUrl } from "../../utils/resolveTestDriveUrl";

type VehicleShowProps = {
  vehicle: Vehicle;
  version_id: number | null;
  vehicle_model?: { id: string; name: string; slug: string } | null;
};

const REVIEWS_ANCHOR_ID = "avis";

const getCompareKey = (
  vehicle: Vehicle,
  version_id?: number | null,
  modelSlug?: string,
) => {
  if (version_id) return `${vehicle.id}:version:${version_id}`;
  if (modelSlug) return `${vehicle.id}:model:${modelSlug}`;
  return `${vehicle.id}:all`;
};

const VehicleShow = (props: VehicleShowProps) => {
  const { vehicle, version_id, vehicle_model } = props;
  const { toggleId, ids } = useVehicleCompare();

  const compareKey = getCompareKey(vehicle, version_id, vehicle_model?.slug);
  const isCompared = ids.some((id) => String(id) === compareKey);

  const basePath = `/vehicles/${vehicle.slug}`;
  const modelPath = vehicle_model
    ? `${basePath}/modeles/${vehicle_model.slug}`
    : basePath;

  const onModelChange = (slug: string) =>
    router.visit(slug ? `${basePath}/modeles/${slug}` : basePath);

  const onVersionChange = (id: string) =>
    router.visit(id ? `${modelPath}?version=${id}` : modelPath);

  const models = vehicle.vehicle_models ?? [];
  const currentVersion = vehicle.vehicle_versions.find(
    (version) => version.id.toString() === version_id?.toString(),
  );
  // Le serializer renvoie TOUTES les versions de la gamme : sur la page d'une
  // appellation, on ne garde que les siennes. Remplace le filtrage par la
  // string modele du code precedent.
  const versions = vehicle_model
    ? vehicle.vehicle_versions.filter(
        (version) =>
          String(version.vehicle_model_id) === String(vehicle_model.id),
      )
    : vehicle.vehicle_versions;

  // Show.tsx sert la fiche de gamme ET celle d'une appellation. Une appellation
  // est plus specifique que la gamme : son image doit primer, sinon toutes les
  // appellations affichent la meme photo de gamme. Sur la fiche de gamme,
  // l'ordre s'inverse -- la photo choisie en admin est l'image de reference.
  const displayedPhoto = vehicle_model
    ? currentVersion?.photo_url || versions[0]?.photo_url || vehicle.photo_url
    : currentVersion?.photo_url || vehicle.photo_url || versions[0]?.photo_url;

  // modelVersions n'est renseigne que sur la page d'une appellation : la fiche
  // de gamme n'emprunte pas le lien d'essai de ses finitions.
  const testDriveUrl = resolveTestDriveUrl({
    vehicle,
    currentVersion,
    modelVersions: vehicle_model ? versions : [],
  });

  return (
    <div className="container mx-auto my-10 max-w-[1280px] px-4">
      <Head title={`${vehicle.brand.title} - ${vehicle.name} - Review car`} />

      <VehicleHeader
        vehicle={vehicle}
        vehicleModel={vehicle_model}
        currentVersion={currentVersion}
        models={models}
        versions={versions}
        photoUrl={displayedPhoto ?? null}
        testDriveUrl={testDriveUrl}
        reviewsAnchor={`#${REVIEWS_ANCHOR_ID}`}
        compare={{
          visible: !!vehicle_model || !!version_id,
          isCompared,
          onToggle: () => toggleId(compareKey),
        }}
        onModelChange={onModelChange}
        onVersionChange={onVersionChange}
      />

      <RatingCriteria
        notes={vehicle.notes}
        reviewsCount={vehicle.reviews.length}
      />

      <div id={REVIEWS_ANCHOR_ID} className="mt-8 scroll-mt-8">
        <Reviews reviews={vehicle.reviews} />
      </div>

      {vehicle.articles && vehicle.articles.length > 0 && (
        <div className="mt-8">
          <SectionHeading title="On en parle" />
          <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {vehicle.articles.map((article) => (
              <Link
                key={article.slug}
                href={`/blog/articles/${article.slug}`}
                className="group flex flex-col overflow-hidden rounded-[14px] border border-slate-100 bg-white text-inherit no-underline shadow-xs transition-shadow hover:shadow-md"
              >
                {article.featured_image_url ? (
                  <img
                    src={article.featured_image_url}
                    alt={article.title}
                    loading="lazy"
                    className="block h-[180px] w-full bg-slate-100 object-cover"
                  />
                ) : (
                  <div
                    className="flex h-[180px] w-full items-center justify-center bg-slate-100 text-slate-400 select-none"
                    aria-hidden="true"
                  >
                    <ImageOff className="h-9 w-9" strokeWidth={1.5} />
                  </div>
                )}
                <div className="flex flex-1 flex-col gap-3 p-5">
                  {article.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-lg bg-[#EFECFF] px-2.5 py-1 text-xs font-medium text-[#412FB2]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <h3 className="text-lg leading-snug font-bold text-balance group-hover:underline">
                    {article.title}
                  </h3>
                  {article.meta_description && (
                    <p className="line-clamp-3 text-sm leading-6 text-muted-foreground">
                      {article.meta_description}
                    </p>
                  )}
                  <div className="mt-auto flex items-center gap-2 pt-1 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {new Intl.DateTimeFormat("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      }).format(new Date(article.published_at))}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <Link
              href="/blog"
              className="text-sm font-semibold text-primary no-underline hover:underline"
            >
              Voir plus →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleShow;
